import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { IMAGES } from "@/lib/data";
import { STRINGS } from "@/lib/i18n";
import { useLanguage } from "@/context/LanguageContext";
import { scrollToId } from "@/lib/scroll";

const lineVariants = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { delay: 0.25 + i * 0.14, duration: 1, ease: [0.22, 1, 0.36, 1] },
  }),
};

const fadeVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: (delay) => ({ opacity: 1, y: 0, transition: { delay, duration: 0.9, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Hero() {
  const { lang } = useLanguage();
  const t = STRINGS[lang].hero;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  const lines = [
    <>{t.lines[0]}</>,
    <>
      <em className="font-medium italic text-blue-400">{t.lines[1]}</em>
    </>,
    <>{t.lines[2]}</>,
  ];

  return (
    <section ref={ref} id="topo" className="spotlight vinyl-texture relative overflow-hidden" data-testid="hero-section">
      <div className="mx-auto grid max-w-7xl gap-14 px-5 pb-24 pt-40 sm:px-8 lg:grid-cols-12 lg:items-center lg:pb-32 lg:pt-48">
        <div className="lg:col-span-7">
          <motion.p
            custom={0.1}
            initial="hidden"
            animate="visible"
            variants={fadeVariants}
            className="font-mono text-[11px] uppercase tracking-[0.3em] text-blue-400/80"
            data-testid="hero-overline"
          >
            {t.overline}
          </motion.p>

          <h1 className="mt-6 font-serif text-4xl font-medium leading-[1.05] tracking-tight text-blue-50/95 sm:text-6xl lg:text-7xl" data-testid="hero-title">
            {lines.map((line, i) => (
              <span key={`${lang}-${i}`} className="block overflow-hidden pb-1">
                <motion.span custom={i} initial="hidden" animate="visible" variants={lineVariants} className="block">
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            custom={0.9}
            initial="hidden"
            animate="visible"
            variants={fadeVariants}
            className="mt-8 max-w-xl text-base leading-relaxed text-slate-300/90"
            data-testid="hero-subtitle"
          >
            {t.subtitle}
          </motion.p>

          <motion.div custom={1.05} initial="hidden" animate="visible" variants={fadeVariants} className="mt-10 flex flex-wrap items-center gap-4">
            <button
              onClick={() => scrollToId("#agendar")}
              data-testid="hero-book-now-button"
              className="group flex items-center gap-3 rounded-full bg-blue-500 px-7 py-3.5 font-mono text-xs font-medium uppercase tracking-[0.18em] text-white shadow-[0_0_28px_rgba(59,130,246,0.35)] transition-colors duration-300 hover:bg-blue-400"
            >
              {t.ctaBook}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => scrollToId("#formatos")}
              data-testid="hero-explore-button"
              className="rounded-full border border-white/15 px-7 py-3.5 font-mono text-xs uppercase tracking-[0.18em] text-slate-200 transition-colors duration-300 hover:border-blue-500/50 hover:text-blue-300"
            >
              {t.ctaExplore}
            </button>
          </motion.div>

          <motion.div
            custom={1.25}
            initial="hidden"
            animate="visible"
            variants={fadeVariants}
            className="mt-14 flex flex-wrap gap-x-12 gap-y-6 border-t border-white/5 pt-8"
            data-testid="hero-stats"
          >
            {t.stats.map(([num, label]) => (
              <div key={label}>
                <p className="font-serif text-3xl text-blue-400">{num}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">{label}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
            style={{ y: imgY }}
            data-testid="hero-image-frame"
          >
            <div className="absolute -inset-6 rounded-full bg-blue-600/10 blur-3xl" aria-hidden="true" />
            <div className="relative overflow-hidden" style={{ clipPath: "polygon(0 3%, 100% 0, 100% 97%, 0 100%)" }}>
              <img
                src={IMAGES.hero}
                alt="Ricardo Oliveira com guitarra e logo IGL"
                className="h-[470px] w-full object-contain brightness-[0.88] sm:h-[570px] lg:h-[650px]"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#05070D]/70 via-[#05070D]/10 to-[#05070D]/30" aria-hidden="true" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
